import { get } from 'lodash';

import { ClassConstructor } from '../globals';

export function getOrDefault<T, V>(obj: T, path: string, defaultValue: V): V {
  return (get(obj, path, defaultValue) ?? defaultValue) as V;
}

/**
 * Checks if the given object is an instance of any of the provided class constructors.
 *
 * @param obj - The object to check.
 * @param types - Class constructors to test against.
 * @returns True if obj is an instance of any type in types, otherwise false.
 */
export function instanceOfAny(obj: any, ...types: ClassConstructor<any>[]): boolean {
  return types.some(type => obj instanceof type);
}

/**
 * Returns the enumerable own property names of `obj`, typed as `(keyof T)[]` instead of `string[]`.
 *
 * This is a thin wrapper around Object.keys.
 * Use it when iterating keys so TypeScript treats each key
 * as `keyof T` (for example, indexing `obj[key]` without casting).
 *
 * @param obj - Non-null object (same constraints as `Object.keys`).
 * @returns Own, enumerable string keys in the same order as `Object.keys`.
 *
 * @example
 * const o = { a: 1, b: 2 } as const;
 * for (const k of objectKeys(o)) {
 *   const v = o[k]; // k is 'a' | 'b'
 * }
 */
export function objectKeys<T extends object>(obj: T): (keyof T)[] {
  return Object.keys(obj) as (keyof T)[];
}
